import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { collection, getDocs, query, where, Timestamp, doc, updateDoc, getDoc } from "firebase/firestore";
import { Order, Shipment, OrderSnapshot, Truck, OrderStatus, ItemSnapshot } from "../types/DB2";
import { db } from "../firebaseConfig";
import { useUserData } from "./UserDataFetcher";
import dbPrefixes from "../config";
import { endOfDay, startOfDay } from "date-fns";
import { isMobileDevice } from "../util/util";
import {
  normalizeOrderItemsSnapshot,
  normalizeShipmentSnapshot,
  OrderSnapshotWithPreserved,
  collectDealerIdsFromShipment,
} from "../utils/normalizeSnapshots";
const dbPrefix = dbPrefixes.productionDbPrefix;

interface DateRange {
  startDate: Date;
  endDate: Date;
  key?: string;
}

const YEAR_IN_MS = 365 * 24 * 60 * 60 * 1000;
const now = new Date();
export const defaultDateRange: DateRange = {
  startDate: startOfDay(new Date(now.getTime() - YEAR_IN_MS)),
  endDate: endOfDay(new Date(now.getTime() + YEAR_IN_MS)),
  key: "selection",
};

const normalizeDealerIdValue = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

const deriveDealerIdFromOrderSnapshot = (
  snapshot: OrderSnapshot | OrderSnapshotWithPreserved | undefined,
  shipmentContext?: Shipment
): string | undefined => {
  const direct = normalizeDealerIdValue((snapshot as any)?.dealerId);
  if (direct) return direct;
  const preserved = normalizeDealerIdValue((snapshot as any)?.preservedOrder?.dealerId);
  if (preserved) return preserved;
  if (!shipmentContext) {
    return undefined;
  }
  const candidateIds = snapshot
    ? collectDealerIdsFromShipment(
        {
          dealerIds: shipmentContext.dealerIds,
          ordersSnapshot: {
            [(snapshot as any)?.orderId || "order"]: snapshot,
          },
        },
        { includeExisting: false }
      )
    : collectDealerIdsFromShipment(shipmentContext, { includeExisting: false });
  if (candidateIds.length) {
    return candidateIds[0];
  }
  const shipmentDealerIds = Array.isArray(shipmentContext.dealerIds)
    ? shipmentContext.dealerIds.reduce<string[]>((acc, value) => {
        const normalized = normalizeDealerIdValue(value);
        if (normalized) acc.push(normalized);
        return acc;
      }, [])
    : [];
  return shipmentDealerIds[0] || undefined;
};

interface ShipmentManagerContextType {
  unassignedOrders: Order[];
  shipments: Shipment[];
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  refreshUnassignedOrders: () => void;
  refreshShipments: () => void;
  assignOrderToShipment: (orderId: string, shipmentId: string) => void;
  moveOrderBetweenShipments: (orderId: string, fromShipmentId: string, toShipmentId: string) => void;
  unassignOrderFromShipment: (orderId: string, shipmentId: string) => void;
  changedItems: any;
  saveChanges: () => void;
  changedShipments: Record<string, Partial<Shipment>>;
  setShipmentTruck: (shipmentId: string, truckId: string) => void;
  setShipmentDrivers: (shipmentId: string, drivers: string[]) => void; // <-- Add this
  setShipmentDate: (shipmentId: string, date: Date) => void; // <-- Add this
  isSaving: boolean;
  manufacturerNotificationEmail: string | null;
  manufacturerNotificationName: string | null;
}

const ShipmentManagerContext = createContext<ShipmentManagerContextType>({
  unassignedOrders: [],
  shipments: [],
  dateRange: defaultDateRange,
  setDateRange: () => {},
  refreshUnassignedOrders: () => {},
  refreshShipments: () => {},
  assignOrderToShipment: () => {},
  moveOrderBetweenShipments: () => {},
  unassignOrderFromShipment: () => {},
  changedItems: [],
  saveChanges: () => {},
  changedShipments: {},
  setShipmentTruck: () => {},
  setShipmentDrivers: () => {}, // <-- Add this
  setShipmentDate: () => {}, // <-- Add this
  isSaving: false,
  manufacturerNotificationEmail: null,
  manufacturerNotificationName: null,
});

export const useShipmentManager = () => useContext(ShipmentManagerContext);

export const ShipmentManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const userData = useUserData();
  const [unassignedOrders, setUnassignedOrders] = useState<Order[]>([]);
  const [initialUnassignedOrders, setInitialUnassignedOrders] = useState<Order[]>([]);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [initialShipments, setInitialShipments] = useState<Shipment[]>([]);
  const [dateRange, setDateRangeState] = useState<DateRange>(defaultDateRange);
  const [changedShipments, setChangedShipments] = useState<Record<string, Partial<Shipment>>>({});
  const [manufacturerNotificationEmail, setManufacturerNotificationEmail] = useState<string | null>(null);
  const [manufacturerNotificationName, setManufacturerNotificationName] = useState<string | null>(null);

  const setDateRange = useCallback(
    (range: DateRange) => {
      const nextStart = range?.startDate ? startOfDay(range.startDate) : defaultDateRange.startDate;
      const nextEnd = range?.endDate ? endOfDay(range.endDate) : defaultDateRange.endDate;
      setDateRangeState({
        startDate: nextStart,
        endDate: nextEnd,
        key: range?.key ?? "selection",
      });
    },
    []
  );

  const normalizeShipment = useCallback(
    (shipment: Shipment, context: string = "ShipmentManagerProvider"): Shipment =>
      normalizeShipmentSnapshot(shipment, context),
    []
  );

  const fetchUnassignedOrders = useCallback(async () => {
    if (!userData) return;
    console.debug('[ShipmentMgr] fetchUnassignedOrders: start', { role: userData?.role, manufacturerId: userData?.manufacturerId, dealerId: userData?.dealerId });
    let q;
    if (userData.role === "manufacturer" && userData.manufacturerId) {
      q = query(
        collection(db, `${dbPrefix}orders`),
        where("manufacturerId", "==", userData.manufacturerId)
      );
    } else if (userData.role === "dealer" && userData.dealerId) {
      q = query(
        collection(db, `${dbPrefix}orders`),
        where("dealerId", "==", userData.dealerId)
      );
    } else {
      setUnassignedOrders([]);
      setInitialShipments([]);
      return;
    }

    const snapshot = await getDocs(q);
    const orders: Order[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as Order;
      const orderId = data.id || docSnap.id;
      if (!data.shipmentIds || data.shipmentIds.length === 0) {
        const normalizedItems = data.itemsSnapshot
          ? normalizeOrderItemsSnapshot(orderId, data.itemsSnapshot, "fetchUnassignedOrders")
          : undefined;
        orders.push({
          ...data,
          id: orderId,
          isDemo: (data as any)?.isDemo ?? false,
          ...(normalizedItems ? { itemsSnapshot: normalizedItems } : {}),
        });
      }
    });
    setUnassignedOrders(orders);
    setInitialUnassignedOrders(orders);
    console.debug('[ShipmentMgr] fetchUnassignedOrders: complete', { count: orders.length });
  }, [userData]);

  const fetchShipments = useCallback(async () => {
    if (!userData || !dateRange.startDate || !dateRange.endDate) return;
    if (userData.role === "dealer" && isMobileDevice()) {
      return;
    }

    const startTimestamp = Timestamp.fromDate(dateRange.startDate);
    const endTimestamp = Timestamp.fromDate(dateRange.endDate);
    console.debug("[ShipmentMgr] fetchShipments: start", {
      role: userData?.role,
      manufacturerId: userData?.manufacturerId,
      dealerId: userData?.dealerId,
      email: (userData as any)?.email,
      start: startTimestamp.toDate().toISOString?.(),
      end: endTimestamp.toDate().toISOString?.(),
    });

    let q: any = null;
    let dealerQueries: Array<{ source: string; queryRef: any }> | null = null;
    let requiresPostFilter = false;

    if (userData.role === "manufacturer" && userData.manufacturerId) {
      q = query(
        collection(db, `${dbPrefix}shipments`),
        where("manufacturerId", "==", userData.manufacturerId),
        where("shipmentDate", ">=", startTimestamp),
        where("shipmentDate", "<=", endTimestamp)
      );
    } else if (userData.role === "dealer" && userData.dealerId) {
      const shipmentsCollection = collection(db, `${dbPrefix}shipments`);
      dealerQueries = [
        {
          source: "dealerIds",
          queryRef: query(shipmentsCollection, where("dealerIds", "array-contains", userData.dealerId)),
        },
      ];
      requiresPostFilter = true;
    } else if (userData.role === "dealer" && !userData.dealerId) {
      console.warn("[ShipmentMgr] fetchShipments: dealer user missing dealerId; skipping query", {
        user: userData,
      });
      setShipments([]);
      setInitialShipments([]);
      return;
    } else if (userData.role === "driver") {
      q = query(
        collection(db, `${dbPrefix}shipments`),
        where("shipmentDate", ">=", startTimestamp),
        where("shipmentDate", "<=", endTimestamp)
      );
    } else {
      setShipments([]);
      setInitialShipments([]);
      return;
    }

    try {
      const snapshotsToProcess: Array<{ source: string; snapshot: any }> = [];
      if (dealerQueries && dealerQueries.length > 0) {
        const dealerIdForLog = userData.dealerId;
        const dealerSnapshots = await Promise.all(
          dealerQueries.map(async ({ source, queryRef }) => {
            try {
              const snapshot = await getDocs(queryRef);
              console.debug("[ShipmentMgr] fetchShipments: dealer query result", {
                dealerId: dealerIdForLog,
                source,
                count: snapshot.size,
              });
              return { source, snapshot };
            } catch (dealerError) {
              console.error("[ShipmentMgr] fetchShipments: dealer query failed", {
                dealerId: dealerIdForLog,
                source,
                error: dealerError,
              });
              return null;
            }
          })
        );
        dealerSnapshots.forEach((entry) => {
          if (entry) snapshotsToProcess.push(entry);
        });
      } else if (q) {
        const snapshot = await getDocs(q);
        console.debug("[ShipmentMgr] fetchShipments: primary query result", {
          role: userData?.role,
          manufacturerId: userData?.manufacturerId,
          dealerId: userData?.dealerId,
          count: snapshot.size,
        });
        snapshotsToProcess.push({ source: "primary", snapshot });
      } else {
        console.warn("[ShipmentMgr] fetchShipments: no query constructed", {
          role: userData?.role,
          manufacturerId: userData?.manufacturerId,
          dealerId: userData?.dealerId,
        });
        setShipments([]);
        setInitialShipments([]);
        return;
      }

      const shipmentsMap = new Map<string, Shipment>();
      const sourceCounts: Record<string, number> = {};
      snapshotsToProcess.forEach(({ source, snapshot }) => {
        sourceCounts[source] = snapshot.size;
        snapshot.forEach((docSnap: any) => {
          const raw = docSnap.data() as Shipment;
          const shipmentId = raw.id || docSnap.id;
          const normalizationContext =
            source === "primary" ? "ShipmentMgr:fetchShipments" : `ShipmentMgr:fetchShipments:${source}`;
          const normalized = normalizeShipment(
            {
              ...raw,
              id: shipmentId,
            },
            normalizationContext
          );
          const normalizedWithFlag: Shipment = {
            ...normalized,
            isDemo: (normalized as any)?.isDemo ?? false,
          };
          const firstOrderId = Object.keys(normalizedWithFlag.ordersSnapshot || {})[0];
          const firstItemKey = firstOrderId
            ? Object.keys(
                normalizedWithFlag.ordersSnapshot[firstOrderId]?.includedItemsSnapshot || {}
              )[0]
            : undefined;
          const firstItem =
            firstOrderId && firstItemKey
              ? normalizedWithFlag.ordersSnapshot[firstOrderId]?.includedItemsSnapshot?.[firstItemKey]
              : undefined;
          const snapshotOrderCount = Object.keys(normalizedWithFlag.ordersSnapshot || {}).length;
          let shipmentDateIso: string | null = null;
          try {
            const rawDate = normalizedWithFlag.shipmentDate as any;
            if (rawDate?.toDate) {
              shipmentDateIso = rawDate.toDate().toISOString();
            } else if (rawDate instanceof Date) {
              shipmentDateIso = rawDate.toISOString();
            } else if (typeof rawDate === "string") {
              shipmentDateIso = rawDate;
            } else if (typeof rawDate === "number") {
              shipmentDateIso = new Date(rawDate).toISOString();
            }
          } catch (parseError) {
            console.warn("[ShipmentMgr] fetchShipments: failed to parse shipmentDate", {
              shipmentId,
              rawDate: normalizedWithFlag.shipmentDate,
              source,
              error: parseError,
            });
          }
          const preExisting = shipmentsMap.has(docSnap.id);
          console.debug("[ShipmentMgr] fetchShipments: normalized shipment", {
            shipmentId,
            status: normalizedWithFlag.status,
            orderCount: normalizedWithFlag.orderIds?.length,
            snapshotOrderCount,
            shipmentDateIso,
            source,
            replacedExisting: preExisting,
            sampleOrderId: firstOrderId,
            sampleItem: firstItem
              ? {
                  id: firstItem.id,
                  orderId: firstItem.orderId,
                  status: firstItem.status,
                }
              : null,
          });
          shipmentsMap.set(docSnap.id, normalizedWithFlag);
        });
      });

      let shipmentsCollected = Array.from(shipmentsMap.values());

      if (requiresPostFilter) {
        const beforeFilter = shipmentsCollected.length;
        shipmentsCollected = shipmentsCollected.filter((shipment) => {
          const rawDate = shipment.shipmentDate as any;
          let shipmentDate: Date | null = null;
          if (rawDate?.toDate) shipmentDate = rawDate.toDate();
          else if (rawDate instanceof Date) shipmentDate = rawDate;
          else if (typeof rawDate === "number") shipmentDate = new Date(rawDate);
          else if (typeof rawDate === "string") {
            const parsed = new Date(rawDate);
            shipmentDate = Number.isNaN(parsed.getTime()) ? null : parsed;
          }
          if (!shipmentDate) return false;
          return shipmentDate >= dateRange.startDate && shipmentDate <= dateRange.endDate;
        });
        console.debug("[ShipmentMgr] fetchShipments: post-filtered dealer shipments", {
          before: beforeFilter,
          after: shipmentsCollected.length,
          dealerId: userData.dealerId,
          email: (userData as any)?.email,
        });
      }

      setShipments(shipmentsCollected);
      setInitialShipments(shipmentsCollected);
      console.debug("[ShipmentMgr] fetchShipments: complete", {
        count: shipmentsCollected.length,
        uniqueCount: shipmentsMap.size,
        sources: Object.entries(sourceCounts).map(([source, count]) => ({
          source,
          count,
        })),
      });
    } catch (error) {
      console.error("[ShipmentMgr] fetchShipments: query failed", {
        role: userData?.role,
        dealerId: userData?.dealerId,
        email: (userData as any)?.email,
        error,
      });
      setShipments([]);
      setInitialShipments([]);
    }
  }, [userData, dateRange, normalizeShipment]);

  const assignOrderToShipment = useCallback(
    (orderId: string, shipmentId: string) => {
      console.debug('[ShipmentMgr] assignOrderToShipment: request', { orderId, shipmentId });
      setUnassignedOrders((prevUnassigned) => {
        const order = prevUnassigned.find((o) => o.id === orderId);
        if (!order) {
          console.warn('[ShipmentMgr] assignOrderToShipment: order not found in unassigned', { orderId });
          return prevUnassigned;
        }
        // Remove from unassigned
        const newUnassigned = prevUnassigned.filter((o) => o.id !== orderId);

        // Add to shipment
        setShipments((prevShipments) =>
          prevShipments.map((shipment) => {
            if (shipment.id !== shipmentId) return shipment;
            // Add orderId to shipment.orderIds if not present
            if (!shipment.orderIds.includes(orderId)) {
              console.debug('[ShipmentMgr] assignOrderToShipment: adding to shipment', { shipmentId, orderId, orderItems: Object.keys(order.itemsSnapshot || {}).length });
              const nextOrdersSnapshot = {
                ...(shipment.ordersSnapshot || {}),
                [orderId]: (() => {
                  const normalizedIncluded = normalizeOrderItemsSnapshot(
                    orderId,
                    order.itemsSnapshot,
                    "assignOrderToShipment:included"
                  );
                  const preservedOrderSnapshot = {
                    ...order,
                    id: order.id || orderId,
                    ...(order.itemsSnapshot
                      ? { itemsSnapshot: normalizedIncluded }
                      : {}),
                  };
                  const normalizedSnapshot: OrderSnapshotWithPreserved = {
                    orderId,
                    orderNumber: order.orderNumber,
                    prefix: (order as any)?.prefix,
                    includedItemsSnapshot: normalizedIncluded,
                    excludedItemsSnapshot: {},
                    preservedOrder: preservedOrderSnapshot,
                  };
                  return normalizedSnapshot;
                })(),
              };
              const updatedShipment = {
                ...shipment,
                orderIds: [...shipment.orderIds, orderId],
                ordersSnapshot: nextOrdersSnapshot,
              };
              const computedDealerIds = collectDealerIdsFromShipment(updatedShipment, {
                includeExisting: false,
              });
              return {
                ...updatedShipment,
                dealerIds: computedDealerIds,
              };
            }
            return shipment;
          })
        );

        console.debug('[ShipmentMgr] assignOrderToShipment: removed from unassigned', { orderId, remaining: newUnassigned.length });
        return newUnassigned;
      });
    },
    []
  );

  const moveOrderBetweenShipments = useCallback(
    (orderId: string, fromShipmentId: string, toShipmentId: string) => {
      console.debug('[ShipmentMgr] moveOrderBetweenShipments: request', { orderId, fromShipmentId, toShipmentId });
      setShipments((prevShipments) => {
        const snapshotSource = prevShipments
          .find((s) => s.id === fromShipmentId)
          ?.ordersSnapshot?.[orderId] as OrderSnapshotWithPreserved | undefined;

        return prevShipments.map((shipment) => {
          if (shipment.id === fromShipmentId) {
            // Remove orderId from fromShipment
            const newOrdersSnapshot = { ...(shipment.ordersSnapshot || {}) };
            delete newOrdersSnapshot[orderId];
            console.debug('[ShipmentMgr] moveOrderBetweenShipments: removed from source', { fromShipmentId, orderId });
            const updatedShipment = {
              ...shipment,
              orderIds: shipment.orderIds.filter((id) => id !== orderId),
              ordersSnapshot: newOrdersSnapshot,
            };
            const computedDealerIds = collectDealerIdsFromShipment(updatedShipment, {
              includeExisting: false,
            });
            return {
              ...updatedShipment,
              dealerIds: computedDealerIds,
            };
          }
          if (shipment.id === toShipmentId) {
            // Add orderId to toShipment
            if (!shipment.orderIds.includes(orderId)) {
              console.debug('[ShipmentMgr] moveOrderBetweenShipments: added to destination', { toShipmentId, orderId, snapshotPresent: !!snapshotSource });
              const normalizedSnapshot: OrderSnapshotWithPreserved = snapshotSource
                ? {
                    ...snapshotSource,
                    orderId: snapshotSource.orderId || orderId,
                    includedItemsSnapshot: normalizeOrderItemsSnapshot(
                      orderId,
                      snapshotSource.includedItemsSnapshot,
                      "moveOrderBetweenShipments:included"
                    ),
                    excludedItemsSnapshot: normalizeOrderItemsSnapshot(
                      orderId,
                      snapshotSource.excludedItemsSnapshot,
                      "moveOrderBetweenShipments:excluded"
                    ),
                    ...(snapshotSource.preservedOrder
                      ? {
                          preservedOrder: {
                            ...snapshotSource.preservedOrder,
                            id: snapshotSource.preservedOrder.id || orderId,
                            ...(snapshotSource.preservedOrder.itemsSnapshot
                              ? {
                                  itemsSnapshot: normalizeOrderItemsSnapshot(
                                    snapshotSource.preservedOrder.id || orderId,
                                    snapshotSource.preservedOrder.itemsSnapshot,
                                    "moveOrderBetweenShipments:preserved"
                                  ),
                                }
                              : {}),
                        },
                      }
                      : {}),
                }
              : {
                  orderId,
                  includedItemsSnapshot: normalizeOrderItemsSnapshot(
                    orderId,
                    undefined,
                    "moveOrderBetweenShipments:newIncluded"
                  ),
                  excludedItemsSnapshot: {},
                };
              const nextOrdersSnapshot = {
                ...(shipment.ordersSnapshot || {}),
                [orderId]: normalizedSnapshot,
              };
              const updatedShipment = {
                ...shipment,
                orderIds: [...shipment.orderIds, orderId],
                ordersSnapshot: nextOrdersSnapshot,
              };
              const computedDealerIds = collectDealerIdsFromShipment(updatedShipment, {
                includeExisting: false,
              });
              return {
                ...updatedShipment,
                dealerIds: computedDealerIds,
              };
            }
          }
          return shipment;
        });
      });
    },
    []
  );
/**
 * Returns a list of changed items: { itemId, oldShipmentId, newShipmentId }
 */
function getChangedItemAssignments(
  initialUnassignedOrders: Order[],
  initialShipments: Shipment[],
  currentUnassignedOrders: Order[],
  currentShipments: Shipment[]
) {
  const initial = flattenItemAssignments(initialUnassignedOrders, initialShipments);
  const current = flattenItemAssignments(currentUnassignedOrders, currentShipments);

  const changed: { itemId: string; oldShipmentId: string | null; newShipmentId: string | null }[] = [];

  const allItemIds = new Set([...Object.keys(initial), ...Object.keys(current)]);
  for (const itemId of allItemIds) {
    const oldShipmentId = initial[itemId] ?? null;
    const newShipmentId = current[itemId] ?? null;
    if (oldShipmentId !== newShipmentId) {
      changed.push({ itemId, oldShipmentId, newShipmentId });
    }
  }
  return changed;
}

  const changedItems = getChangedItemAssignments(
    initialUnassignedOrders,
    initialShipments,
    unassignedOrders,
    shipments
  );

  const saveChanges = useCallback(async () => {
    // Save changed items first (existing logic)
    console.debug('[ShipmentMgr] saveChanges: begin', { changedItemsCount: changedItems.length, changedShipmentsCount: Object.keys(changedShipments).length });
    setIsSaving(true);
    const shipmentsNeedingDealerSync = new Set<string>();
    changedItems.forEach(({ oldShipmentId, newShipmentId }) => {
      if (oldShipmentId) shipmentsNeedingDealerSync.add(oldShipmentId);
      if (newShipmentId) shipmentsNeedingDealerSync.add(newShipmentId);
    });
    Object.keys(changedShipments).forEach((shipmentId) => shipmentsNeedingDealerSync.add(shipmentId));
    for (const { itemId, newShipmentId } of changedItems) {
      // Find the order containing this item
      let orderId: string | undefined;
      let orderDoc: Order | undefined;

      // Search in unassignedOrders
      for (const order of unassignedOrders) {
        if (order.itemsSnapshot && order.itemsSnapshot[itemId]) {
          orderId = order.id;
          orderDoc = order;
          break;
        }
      }
      // If not found, search in shipments' ordersSnapshot
      if (!orderId) {
        for (const shipment of shipments) {
          for (const oid of shipment.orderIds) {
            const snap = shipment.ordersSnapshot?.[oid];
            if (snap && snap.includedItemsSnapshot && snap.includedItemsSnapshot[itemId]) {
              orderId = oid;
              // Try to find the original order in unassignedOrders or initialUnassignedOrders
              orderDoc = unassignedOrders.find(o => o.id === oid) ||
                         initialUnassignedOrders.find(o => o.id === oid);
              break;
            }
          }
          if (orderId) break;
        }
      }
      if (!orderId) continue; // Can't find order, skip

      // Update the item in Firestore
      try {
        // Items are stored in the items collection
        const itemRef = doc(db, `${dbPrefix}items`, itemId);
        console.debug('[ShipmentMgr] saveChanges: update item', { itemId, newShipmentId });
        await updateDoc(itemRef, {
          shipmentIds: newShipmentId ? [newShipmentId] : [],
        });
        console.debug('[ShipmentMgr] saveChanges: updated item OK', { itemId });
      } catch (e) {
        console.error(`Failed to update item ${itemId}:`, e);
      }
    }

    // Now save changed shipments
    const processedShipments = new Set<string>();
    for (const [shipmentId, changedFields] of Object.entries(changedShipments)) {
      // Find the initial shipment state
      const initial = initialShipments.find(s => s.id === shipmentId);
      if (!initial) continue;
      const currentShipment = shipments.find((s) => s.id === shipmentId);
      const computedDealerIds = currentShipment
        ? collectDealerIdsFromShipment(currentShipment, { includeExisting: false })
        : undefined;

      // Build an update object with only fields that have changed and are not blank
      const updateObj: Record<string, any> = {};
      for (const [key, value] of Object.entries(changedFields)) {
        // Skip blank values (null, undefined, empty string)
        if (
          value === undefined ||
          value === null
        ) {
          continue;
        }
        // Only update if value is different from initial
        if (
          key === "shipmentDate"
            ? (value as Timestamp)?.toMillis?.() !== initial.shipmentDate?.toMillis?.()
            : JSON.stringify(value) !== JSON.stringify((initial as any)[key])
        ) {
          updateObj[key] = value;
        }
      }
      if (computedDealerIds) {
        updateObj.dealerIds = computedDealerIds;
      }
      if (Object.keys(updateObj).length > 0) {
        try {
          const shipmentRef = doc(db, `${dbPrefix}shipments`, shipmentId);
          console.debug('[ShipmentMgr] saveChanges: update shipment', { shipmentId, fields: Object.keys(updateObj) });
          await updateDoc(shipmentRef, updateObj);
          console.debug('[ShipmentMgr] saveChanges: updated shipment OK', { shipmentId });
        } catch (e) {
          console.error(`Failed to update shipment ${shipmentId}:`, e);
        }
      }
      processedShipments.add(shipmentId);
    }
    for (const shipmentId of shipmentsNeedingDealerSync) {
      if (processedShipments.has(shipmentId)) continue;
      const currentShipment = shipments.find((s) => s.id === shipmentId);
      if (!currentShipment) continue;
      const computedDealerIds = collectDealerIdsFromShipment(currentShipment, { includeExisting: false });
      const updateObj: Record<string, any> = {
        dealerIds: computedDealerIds,
      };
      try {
        const shipmentRef = doc(db, `${dbPrefix}shipments`, shipmentId);
        console.debug('[ShipmentMgr] saveChanges: sync dealerIds only', { shipmentId, dealerIds: computedDealerIds });
        await updateDoc(shipmentRef, updateObj);
      } catch (error) {
        console.error(`[ShipmentMgr] saveChanges: failed to sync dealerIds for shipment ${shipmentId}`, error);
      }
    }
    setTimeout(() => {
      console.debug('[ShipmentMgr] saveChanges: end');
      setIsSaving(false);
      const newInitialShipments = shipments.map(s => ({...s}));
      setInitialShipments(newInitialShipments);
      const newUnassignedOrders = unassignedOrders.map(o => ({...o}));
      setInitialUnassignedOrders(newUnassignedOrders);
      setChangedShipments({});
    }, 2000);
  }, [changedItems, changedShipments, initialShipments]);

  const unassignOrderFromShipment = useCallback(
    (orderId: string, shipmentId: string) => {
      console.debug('[ShipmentMgr] unassignOrderFromShipment: request', { orderId, shipmentId });
      // Remove from shipment
      setShipments((prevShipments) =>
        prevShipments.map((shipment) => {
          if (shipment.id === shipmentId) {
            const newOrdersSnapshot = { ...(shipment.ordersSnapshot || {}) };
            delete newOrdersSnapshot[orderId];
            console.debug('[ShipmentMgr] unassignOrderFromShipment: removed from shipment snapshot', { shipmentId, orderId });
            const updatedShipment = {
              ...shipment,
              orderIds: shipment.orderIds.filter((id) => id !== orderId),
              ordersSnapshot: newOrdersSnapshot,
            };
            const computedDealerIds = collectDealerIdsFromShipment(updatedShipment, {
              includeExisting: false,
            });
            return {
              ...updatedShipment,
              dealerIds: computedDealerIds,
            };
          }
          return shipment;
        })
      );
      // Add back to unassignedOrders if not already present
      setUnassignedOrders((prevUnassigned) => {
        // Only add if not already present
        if (prevUnassigned.some((o) => o.id === orderId)) return prevUnassigned;
        // Find the order in shipments (could use initialUnassignedOrders as a backup)
        let foundOrder: Order | undefined;
        shipments.forEach((shipment) => {
          if (shipment.id === shipmentId) {
            // Try to find order snapshot in shipment.ordersSnapshot
            if (shipment.ordersSnapshot && shipment.ordersSnapshot[orderId]) {
              // You may want to reconstruct the Order object here
              const foundSnapshot = {
                ...(shipment.ordersSnapshot[orderId] as OrderSnapshotWithPreserved),
                orderId,
              };
              const derivedDealerId = deriveDealerIdFromOrderSnapshot(foundSnapshot, shipment);
              foundOrder =
                foundSnapshot.preservedOrder ||
                orderFromSnapshot(foundSnapshot, {
                  dealerId: derivedDealerId,
                  manufacturerId: shipment.manufacturerId,
                  createdAt: shipment.shipmentDate,
                  shipmentIds: [],
                  isDemo: (shipment as any)?.isDemo ?? false,
                });
            }
          }
        });
        console.debug('[ShipmentMgr] unassignOrderFromShipment: add back to unassigned', { orderId, found: !!foundOrder });
        // Fallback: try to find in initialUnassignedOrders
        if (!foundOrder) {
          foundOrder = initialUnassignedOrders.find((o) => o.id === orderId);
        }
        if (!foundOrder) {
          return prevUnassigned;
        }
        const normalizedItems = foundOrder.itemsSnapshot
          ? normalizeOrderItemsSnapshot(
              foundOrder.id || orderId,
              foundOrder.itemsSnapshot,
              "unassignOrderFromShipment"
            )
          : undefined;
        const normalizedOrder: Order = {
          ...foundOrder,
          id: foundOrder.id || orderId,
          isDemo: (foundOrder as any)?.isDemo ?? false,
          ...(normalizedItems ? { itemsSnapshot: normalizedItems } : {}),
        };
        return [...prevUnassigned, normalizedOrder];
      });
    },
    [shipments, initialUnassignedOrders]
  );

  // Set truck for a shipment and track as a change
  const setShipmentTruck = useCallback((shipmentId: string, truckId: string) => {
    setShipments((prev) =>
      prev.map((shipment) =>
        shipment.id === shipmentId ? { ...shipment, truckId } : shipment
      )
    );
    setChangedShipments((prev) => ({
      ...prev,
      [shipmentId]: {
        ...(prev[shipmentId] || {}),
        truckId,
      },
    }));
  }, []);

  // Set drivers for a shipment and track as a change
  const setShipmentDrivers = useCallback((shipmentId: string, drivers: string[]) => {
    setShipments((prev) =>
      prev.map((shipment) =>
        shipment.id === shipmentId ? { ...shipment, drivers } : shipment
      )
    );
    setChangedShipments((prev) => ({
      ...prev,
      [shipmentId]: {
        ...(prev[shipmentId] || {}),
        drivers,
      },
    }));
  }, []);

  // Set shipment date for a shipment and track as a change
  const setShipmentDate = useCallback((shipmentId: string, date: Date) => {
    setShipments((prev) =>
      prev.map((shipment) =>
        shipment.id === shipmentId
          ? { ...shipment, shipmentDate: Timestamp.fromDate(date) }
          : shipment
      )
    );
    setChangedShipments((prev) => ({
      ...prev,
      [shipmentId]: {
        ...(prev[shipmentId] || {}),
        shipmentDate: Timestamp.fromDate(date),
      },
    }));
  }, []);

  useEffect(() => {
    fetchUnassignedOrders();
  }, [userData, fetchUnassignedOrders]);

  useEffect(() => {
    if (userData?.role === "dealer" && isMobileDevice()) {
      return;
    }
    fetchShipments();
  }, [userData, dateRange, fetchShipments]);

  useEffect(() => {
    const fetchManufacturerContact = async () => {
      if (!userData || !userData.manufacturerId) {
        setManufacturerNotificationEmail(null);
        setManufacturerNotificationName(null);
        return;
      }
      try {
        const candidatePaths: Array<[string, string]> = [
          ["manufacturers", userData.manufacturerId],
          [`${dbPrefix}manufacturers`, userData.manufacturerId],
        ];

        let contactData: Record<string, any> | null = null;
        for (const [collectionName, docId] of candidatePaths) {
          if (!collectionName) continue;
          const mfRef = doc(db, collectionName, docId);
          const snapshot = await getDoc(mfRef);
          if (snapshot.exists()) {
            contactData = snapshot.data() as Record<string, any>;
            break;
          }
        }

        if (contactData) {
          const email =
            typeof contactData.notificationEmail === "string"
              ? contactData.notificationEmail.trim()
              : "";
          setManufacturerNotificationEmail(email.length ? email : null);
          const name =
            typeof contactData.name === "string" &&
            contactData.name.trim().length
              ? contactData.name.trim()
              : null;
          setManufacturerNotificationName(name);
        } else {
          setManufacturerNotificationEmail(null);
          setManufacturerNotificationName(null);
        }
      } catch (error) {
        console.error("[ShipmentMgr] fetchManufacturerContact failed", {
          manufacturerId: userData?.manufacturerId,
          error,
        });
        setManufacturerNotificationEmail(null);
        setManufacturerNotificationName(null);
      }
    };

    fetchManufacturerContact();
  }, [userData?.manufacturerId]);

  /**
 * Utility to flatten all ItemSnapshots from orders and shipments into a map of itemId -> { shipmentId | null }
 */
function flattenItemAssignments(
  unassignedOrders: Order[],
  shipments: Shipment[]
): Record<string, string | null> {
  const map: Record<string, string | null> = {};

  // Items in unassigned orders: shipmentId is null
  for (const order of unassignedOrders) {
    if (order.itemsSnapshot) {
      for (const itemId of Object.keys(order.itemsSnapshot)) {
        map[itemId] = null;
      }
    }
  }

  // Items in shipments: shipmentId is the shipment's id
  for (const shipment of shipments) {
    for (const orderSnapshot of Object.values(shipment.ordersSnapshot || {})) {
      if (orderSnapshot.includedItemsSnapshot) {
        for (const itemId of Object.keys(orderSnapshot.includedItemsSnapshot)) {
          map[itemId] = shipment.id;
        }
      }
    }
  }

  return map;
}


  return (
    <ShipmentManagerContext.Provider
      value={{
        unassignedOrders,
        shipments,
        dateRange,
        setDateRange,
        refreshUnassignedOrders: fetchUnassignedOrders,
        refreshShipments: fetchShipments,
        assignOrderToShipment,
        moveOrderBetweenShipments,
        unassignOrderFromShipment,
        changedItems,
        saveChanges,
        changedShipments,
        setShipmentTruck,
        setShipmentDrivers, // <-- Add this
        setShipmentDate, // <-- Add this
        isSaving,
        manufacturerNotificationEmail,
        manufacturerNotificationName,
      }}
    >
      {children}
    </ShipmentManagerContext.Provider>
  );
};

/**
 * Reconstructs an Order object from an OrderSnapshot.
 * You must provide dealerId, manufacturerId, and createdAt externally if not present in the snapshot.
 */
export function orderFromSnapshot(
  snapshot: OrderSnapshot,
  opts: {
    dealerId?: string;
    manufacturerId?: string;
    createdAt?: any; // Should be a Timestamp
    status?: string;
    shipmentIds?: string[];
    processed?: boolean;
    processedAt?: any;
    issueSummary?: {
      hasIssues: boolean;
      damagedCount?: number;
      missingCount?: number;
    };
    isDemo?: boolean;
  }
): Order {
  const allowedStatuses = new Set<OrderStatus>(["open", "fulfilled", "cancelled"]);
  const resolvedStatus: OrderStatus =
    typeof opts.status === "string" && allowedStatuses.has(opts.status as OrderStatus)
      ? (opts.status as OrderStatus)
      : "open";
  const resolvedDealerId =
    normalizeDealerIdValue(opts.dealerId) ||
    deriveDealerIdFromOrderSnapshot(snapshot as OrderSnapshotWithPreserved) ||
    "";

  const normalizedIncluded = normalizeOrderItemsSnapshot(
    snapshot.orderId,
    snapshot.includedItemsSnapshot,
    "orderFromSnapshot:included"
  );
  const normalizedExcluded = normalizeOrderItemsSnapshot(
    snapshot.orderId,
    snapshot.excludedItemsSnapshot,
    "orderFromSnapshot:excluded"
  );
  const itemIds = Object.keys({
    ...normalizedIncluded,
    ...normalizedExcluded,
  });

  return {
    id: snapshot.orderId,
    dealerId: resolvedDealerId,
    manufacturerId: opts.manufacturerId || '',
    createdAt: opts.createdAt || null,
    status: resolvedStatus,
    itemIds,
    shipmentIds: opts.shipmentIds || [],
    itemsSnapshot: normalizedIncluded,
    destinations: snapshot.destinations || [],
    destinationsGeo: snapshot.destinationsGeo || {},
    processed:
      typeof snapshot.processed === "boolean"
        ? snapshot.processed
        : typeof opts.processed === "boolean"
        ? opts.processed
        : undefined,
    processedAt: snapshot.processedAt || opts.processedAt,
    issueSummary: snapshot.issueSummary || opts.issueSummary,
    isDemo: (snapshot as any)?.isDemo ?? opts.isDemo ?? false,
  };
}
